"use client";

import * as React from "react";
import { CartesianGrid, Line, LineChart, XAxis } from "recharts";

import {
	ChartContainer,
	ChartLegend,
	ChartLegendContent,
	ChartTooltip,
	ChartTooltipContent,
	seriesColor,
} from "./chart";
import {
	CHART_AXIS_PROPS,
	CHART_GRID_PROPS,
	type CartesianChartProps,
} from "./chart-cartesian";

export type ChartLineProps = CartesianChartProps & {
	/**
	 * - `default` — smoothed line(s).
	 * - `dots` — a dot marker on every point.
	 * - `step` — stepped interpolation for state changes.
	 */
	variant?: "default" | "dots" | "step";
	/**
	 * Series keys drawn as a dotted line, e.g. a target or reference line.
	 * Every other series stays solid.
	 */
	dashedSeries?: string[];
	/**
	 * Grows the line in on mount. Turn off when the chart is rasterized right
	 * after it renders, which would otherwise capture it part-drawn.
	 */
	isAnimationActive?: boolean;
};

/**
 * Line chart built on the shared `ChartContainer` primitives. Palette-agnostic:
 * every series colour is read from `config` as `var(--color-<key>)`.
 */
export function ChartLine({
	data,
	config,
	series,
	categoryKey,
	variant = "default",
	dashedSeries,
	className,
	valueFormatter,
	categoryFormatter,
	hideTooltip = false,
	showLegend,
	isAnimationActive = true,
}: ChartLineProps) {
	const curveType = variant === "step" ? "step" : "monotone";
	const withDots = variant === "dots";
	const withLegend = showLegend ?? series.length > 1;
	const dashed = new Set(dashedSeries);

	return (
		<ChartContainer config={config} className={className}>
			<LineChart accessibilityLayer data={data}>
				<CartesianGrid {...CHART_GRID_PROPS} />
				<XAxis
					dataKey={categoryKey}
					{...CHART_AXIS_PROPS}
					tickFormatter={categoryFormatter}
				/>
				{!hideTooltip && (
					<ChartTooltip
						content={<ChartTooltipContent valueFormatter={valueFormatter} />}
					/>
				)}
				{withLegend && <ChartLegend content={<ChartLegendContent />} />}
				{series.map((key) => (
					<Line
						key={key}
						dataKey={key}
						type={curveType}
						stroke={seriesColor(key)}
						strokeWidth={2}
						strokeDasharray={dashed.has(key) ? "2 4" : undefined}
						dot={withDots ? { fill: seriesColor(key) } : false}
						activeDot={{ r: 4 }}
						isAnimationActive={isAnimationActive}
					/>
				))}
			</LineChart>
		</ChartContainer>
	);
}
